import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { OrganisationEntity } from '../../../../Models/Entities';
import { switchOrganisationsAsync } from 'Global/Organisations/OrganisationsStore';
import classNames from 'classnames';
import useStore from '../../../../Hooks/useStore';
import { themeStore } from 'Models/ThemeStore';
import { useEffect, useRef } from 'react';
import { observer } from 'mobx-react';

export interface CustomDropDownProps {
	organisations: OrganisationEntity[] | null;
	show: boolean;
	toggleShowMenu: () => void;
	setShowOrganisationSelection: (show: boolean) => void;
}

function OrganisationDropDown({
	organisations,
	show,
	toggleShowMenu,
	setShowOrganisationSelection,
}: CustomDropDownProps) {
	const navigate = useNavigate();

	const dropdownRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		// Function to check if click is outside the dropdown
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				toggleShowMenu();
			}
		};

		// Add event listener when dropdown is shown
		if (show) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		// Cleanup listener when component unmounts or when dropdown closes
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [show, toggleShowMenu, setShowOrganisationSelection]);

	if (!organisations) {
		return null;
	}

	// eslint-disable-next-line react/destructuring-assignment
	const tags = organisations.map(org => org.tag);
	const uniqueTags = [...new Set(tags)].sort((a, b) => (a > b ? 1 : -1));

	const getLogoForOrganisation = (org: OrganisationEntity) => {
		const configTheme = window.themeData.find(x => x.organisation?.id === org.id);
		if (configTheme && configTheme?.brandLogoPrimaryId) {
			return `/api/files/${configTheme?.brandLogoPrimaryId}`;
		}
		return themeStore.logoPrimaryDarkURL;
	};

	const sections = [] as JSX.Element[];
	uniqueTags.forEach(tag => {
		sections.push(
			<div key={tag}>
				<p className="destination-tag__header">{tag.toUpperCase()}</p>
				<div className="destination-tag__body">
					{organisations
						?.filter(org => org.tag === tag)
						?.sort((a, b) => a.name.localeCompare(b.name))
						?.map(org => {
							return (
								<div
									className="item"
									key={org.id}
									onClick={e => {
										switchOrganisationsAsync(org.id);
										setShowOrganisationSelection(false);
										if (!org?.hasFerries) {
											navigate('/upcoming-events');
										}
									}}
									onKeyDown={() => {}}
								>
									<div className="brand-logo__container">
										<img
											className="brand-logo__image"
											src={getLogoForOrganisation(org)}
											alt="brand-logo"
										/>
									</div>
									<p>{org.name}</p>
								</div>
							);
						})}
				</div>
			</div>,
		);
	});
	return (
		<div ref={dropdownRef} className={classNames('custom-dropdown-menu', show ? 'show' : '')}>
			{sections}
		</div>
	);
}

export default observer(OrganisationDropDown);
